import { guid } from '@honeybook/hbui/dist/utils/shared.utils';
import type { CXRootStore } from '../../stores/cx-root.store';
import { last } from 'lodash';
import { activityWatcher } from '~/components/ClientExperience/OneFlow/services/activity-watcher.service';
import axios from 'axios';
import { when } from 'mobx';
import { UAParser } from 'ua-parser-js';

export enum SessionEventType {
    START = 'userActive',
    PAUSE = 'userPause',
    RESUME = 'userResume',
    ROUTE_CHANGE = 'routechanged',
    END = 'userInactive'
}

export class CXFlowSessionService {
    rootStore: CXRootStore;

    sessionDetails = {
        id: guid(),
        clientId: '0',
        flowId: '0',
        userAgent: navigator.userAgent,
        isMobile: !!navigator.userAgent.match(
            /(IEMobile|Opera Mini|iPhone|iPad|iPod|BlackBerry|Android)/
        ),
        start: new Date(),
        lastTimestamp: new Date(),
        lastUrl: window.location.pathname,
        attentionPeriod: 0,
        clientEmail: '',
        pagesCount: null,
        companyId: null,
        referrer: document?.referrer,
        location: null,
        browser: null,
        device: null,
        leadId: '0'
    };

    getUserLocation = async () => {
        try {
            const location = await axios.get(
                'https://geolocation-db.com/json/'
            );

            return location.data;
        } catch {
            return {};
        }
    };

    getUserAgentData = () => {
        try {
            const uaData = new UAParser();
            return {
                device: uaData.getDevice(),
                browser: uaData.getBrowser()
            };
        } catch {
            return {
                device: null,
                browser: null
            };
        }
    };

    getLeadId = () => {
        const leadId = localStorage.getItem('leadId');
        if (leadId) {
            return leadId;
        } else {
            const newLeadId = guid();
            localStorage.setItem('leadId', newLeadId);
            return newLeadId;
        }
    };

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;

        if (window.isProduction) {
            // Creating the url change event (hard-coded)
            this.createUrlChangedEvent();

            // using mobx observer to sent first attention and set the infra only when there are essential props
            when(this.isReadyToSendAttention, async () => {
                this.sessionDetails.leadId = this.getLeadId();
                this.sessionDetails.location = await this.getUserLocation();

                const userAgentData = this.getUserAgentData();
                this.sessionDetails.browser = userAgentData.browser;
                this.sessionDetails.device = userAgentData.device;

                this.sessionDetails.clientId =
                    this.rootStore?.userStore?.currUser?._id;

                this.sessionDetails.clientEmail =
                    this.rootStore?.userStore?.currUser?.email;

                this.sessionDetails.pagesCount =
                    this.rootStore?.flowStore?.flow?.pages?.length;

                this.sessionDetails.companyId =
                    this.rootStore?.userStore?.currUser?.company_id;

                this.sessionDetails.flowId =
                    this.rootStore?.flowStore?.flow?._id;

                // Setting START event after stores are created
                this.sentAttentionEvent({
                    eventType: SessionEventType.START
                });

                activityWatcher({
                    idleTimeInSeconds: 120,
                    pauseAction: this.firePause,
                    resumeAction: this.fireResume
                });
            });

            // Adding other events
            window.addEventListener('locationchange', this.fireRouteChanged);
            window.addEventListener('beforeunload', this.fireUserInactive);
        }
    }

    isReadyToSendAttention = () => {
        return !!this.rootStore?.flowStore?.flow;
    };

    createUrlChangedEvent = () => {
        let oldPushState = history.pushState;
        history.pushState = function pushState() {
            let ret = oldPushState.apply(this, arguments);
            window.dispatchEvent(new Event('pushstate'));
            window.dispatchEvent(new Event('locationchange'));
            return ret;
        };

        let oldReplaceState = history.replaceState;
        history.replaceState = function replaceState() {
            let ret = oldReplaceState.apply(this, arguments);
            window.dispatchEvent(new Event('replacestate'));
            window.dispatchEvent(new Event('locationchange'));
            return ret;
        };

        window.addEventListener('popstate', () => {
            window.dispatchEvent(new Event('locationchange'));
        });
    };

    fireResume = () => {
        this.sentAttentionEvent({
            eventType: SessionEventType.RESUME
        });
    };

    firePause = () => {
        this.sentAttentionEvent({
            eventType: SessionEventType.PAUSE
        });
    };

    fireRouteChanged = () => {
        this.sentAttentionEvent({
            eventType: SessionEventType.ROUTE_CHANGE
        });
    };

    fireUserInactive = () => {
        this.sentAttentionEvent({
            eventType: SessionEventType.END
        });
    };

    getPageIndex = url =>
        url ? +last(url.split('/')).toString().split('-')[0] : undefined;

    getFlowId = url => (url ? url.split('/flow/')[1].split('/')[0] : undefined);

    sentAttentionEvent = ({ eventType }) => {
        const now = new Date();

        let duration = 0;

        if (eventType !== SessionEventType.START) {
            duration = Math.round(
                (now.getTime() - this.sessionDetails.lastTimestamp.getTime()) /
                    1000
            );
        }

        const fromUrl =
            eventType === SessionEventType.START
                ? undefined
                : this.sessionDetails.lastUrl;

        const toUrl = window.location.pathname;

        const params = {
            sessionId: this.sessionDetails.id,
            attentionPeriod: this.sessionDetails.attentionPeriod + 1,
            eventType: eventType,
            userId: this.sessionDetails.clientId,
            userEmail: this.sessionDetails.clientEmail,
            companyId: this.sessionDetails.companyId,
            isAdminLogin: false,
            fromUrl,
            toUrl,
            host: window.location.host,
            timestamp: now.toJSON(),
            durationInSeconds: duration,
            userAgent: this.sessionDetails.userAgent,
            isMobile: this.sessionDetails.isMobile,
            flowId: this.sessionDetails.flowId,
            fromPageIndex: this.getPageIndex(fromUrl),
            toPageIndex: this.getPageIndex(toUrl),
            pagesCount: this.sessionDetails.pagesCount,
            referrer: this.sessionDetails.referrer,
            location: this.sessionDetails.location,
            broswer: this.sessionDetails.browser,
            device: this.sessionDetails.device,
            leadId: this.sessionDetails.leadId
        };

        this.sendRequest(params);

        this.sessionDetails.lastTimestamp = new Date(now);
        this.sessionDetails.lastUrl = window.location.pathname;
        this.sessionDetails.attentionPeriod++;
    };

    sendRequest = dataToSend => {
        axios.post('https://streams.honeybook.com/attention-client', {
            data: dataToSend
        });
    };
}
